// src/components/LessonPlanGuideOverlay.js
import React, { useState, useLayoutEffect, useRef } from 'react';
import useGuideScrollExtension from '../utils/useGuideScrollExtension';

// Below this viewport width the overlay is hidden and the toggle disappears.
const BREAKPOINT = 900;
const TIP = 15; // CSS triangle half-width / height (px)
const SPOT_PAD = 12;

const LessonPlanGuideOverlay = ({
    onClose,
    // --- dual-mode props (default to "show all" for backwards compat) ---
    mode             = 'manual', // 'auto' | 'manual'
    autoStep         = null,     // 1|3|4 — only used when mode === 'auto'
    hasDocuments     = true,
    isAnalysisComplete = true,
    hasSchedules     = true,
    anonymousProfiles = false,
}) => {
    const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth < BREAKPOINT);
    const [positions, setPositions] = useState(null);
    const box1Ref = useRef(null);
    const box3Ref = useRef(null); // Generate Lesson (step 2 visually)
    const box4Ref = useRef(null); // Add Your Class (step 3 visually)

    useLayoutEffect(() => {
        // If targeting the schedule button in auto mode, scroll it into view first
        if (mode === 'auto' && autoStep === 4) {
            const scheduleEl = document.querySelector('[data-guide="lp-create-schedule"]');
            if (scheduleEl) {
                const rect = scheduleEl.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                    scheduleEl.scrollIntoView({ behavior: 'instant', block: 'center' });
                }
            }
        }

        const calculate = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;

            if (vw < BREAKPOINT) {
                setIsSmallScreen(true);
                setPositions(null);
                return;
            }
            setIsSmallScreen(false);

            // Which steps are visible for this render
            const active = mode === 'auto'
                ? new Set([autoStep])
                : new Set([
                    1,
                    ...(isAnalysisComplete || hasSchedules ? [3] : []),
                    ...(hasSchedules                       ? [4] : []),
                ]);

            const docsEl     = document.querySelector('[data-guide="lp-documents"]');
            const analysisEl = document.querySelector('[data-guide="lp-analysis"]');
            const scheduleEl = document.querySelector('[data-guide="lp-create-schedule"]');

            // Step 1: top of dashed border for Y, centre of text span for X
            const gridEl    = document.querySelector('[data-guide="lp-analyze-btn"]:not(span)');
            const dashedDiv = gridEl?.querySelector('[style*="dashed"]') ?? null;
            const step1SpanEl = dashedDiv?.querySelector('span') ?? dashedDiv ?? docsEl ?? analysisEl;
            const step1El     = dashedDiv ?? docsEl ?? analysisEl;

            // Steps 3/4: generate button and create-schedule button
            const step3El   = document.querySelector('[data-guide="lp-analyze-btn"] .btn-coral')
                           ?? document.querySelector('[data-guide="lp-analyze-btn"] .btn-teal')
                           ?? document.querySelector('[data-guide="lp-analyze-btn"]');

            if (!step1El || !box1Ref.current || !box3Ref.current || !box4Ref.current) return;

            const de  = step1El.getBoundingClientRect();
            const se  = scheduleEl?.getBoundingClientRect() ?? null;
            const anl = step3El?.getBoundingClientRect()    ?? null;

            const b1 = box1Ref.current.getBoundingClientRect();
            const b3 = box3Ref.current.getBoundingClientRect();
            const b4 = box4Ref.current.getBoundingClientRect();

            const pad = 10;
            const newPos = {};

            // ── Step 1: arrowDown — tip on dashed border, X from span centre ───────────
            if (active.has(1)) {
                const span = step1SpanEl.getBoundingClientRect();
                const tipX = span.left + span.width / 2;
                const tipY = de.top;
                const boxLeft   = Math.max(pad, Math.min(tipX - b1.width / 2, vw - b1.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b1.width - TIP);
                newPos.box1 = { bottom: vh - tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Step 3 (visual 2): arrowDown — tip at top of Generate Lesson button ───
            if (active.has(3) && anl) {
                const tipX = anl.left + anl.width / 2;
                const tipY = anl.top;
                const boxLeft   = Math.max(pad, Math.min(tipX - b3.width / 2, vw - b3.width - pad));
                const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b3.width - TIP);
                newPos.box3 = { bottom: vh - tipY + TIP, left: boxLeft, arrowLeft };
            }

            // ── Step 4 (visual 3): arrowLeft — tip just right of Create Schedule button
            if (active.has(4) && se) {
                const tipX   = se.right + 5;
                const tipY   = se.top + se.height / 2;
                const boxLeft = Math.min(tipX + TIP, vw - b4.width - pad);
                const boxTop  = Math.max(pad, Math.min(tipY - b4.height / 2, vh - b4.height - pad));
                newPos.box4 = { top: boxTop, left: boxLeft };
            }

            // ── Spotlight ──
            if (mode === 'auto') {
                let targetRect = null;
                if (autoStep === 1)      targetRect = de;
                else if (autoStep === 3) targetRect = anl;
                else if (autoStep === 4) targetRect = se;
                if (targetRect) {
                    newPos.spotlights = [{ x: targetRect.left, y: targetRect.top, w: targetRect.width, h: targetRect.height }];
                }
            }

            setPositions(newPos);
        };

        calculate();
        window.addEventListener('resize', calculate);
        window.addEventListener('scroll', calculate, { passive: true });
        return () => {
            window.removeEventListener('resize', calculate);
            window.removeEventListener('scroll', calculate);
        };
    }, [mode, autoStep, hasDocuments, isAnalysisComplete, hasSchedules, anonymousProfiles]);

    useGuideScrollExtension(!isSmallScreen);

    if (isSmallScreen) return null;

    // ── Styles ───────────────────────────────────────────────────────────────────

    const overlayStyle = {
        position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
        zIndex: 9999,
        cursor: 'pointer',
        ...(mode === 'manual' ? { backgroundColor: 'rgba(45, 64, 66, 0.6)' } : {}),
        fontFamily: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif',
    };

    const spots = positions?.spotlights ?? [];

    const boxBase = {
        position: 'fixed',
        backgroundColor: 'white',
        border: '3px solid #2D4042',
        borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px',
        padding: '20px',
        boxShadow: '8px 8px 0px rgba(0,0,0,0.3)',
        pointerEvents: mode === 'auto' ? 'auto' : 'none',
    };

    const markerStyle = {
        position: 'absolute', top: '-15px', left: '-15px',
        width: '35px', height: '35px', backgroundColor: '#75B9BE', color: 'white',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 'bold', fontSize: '1.2rem', border: '2px solid #2D4042',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.2)',
    };

    const titleStyle = {
        margin: '0 0 5px 0', color: '#2D4042', fontSize: '1.1rem',
        fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px',
    };

    const textStyle = { margin: 0, color: '#555', fontSize: '0.95rem', lineHeight: '1.4' };

    const getBoxStyle = (posData, maxWidth) => {
        if (!posData) return { ...boxBase, maxWidth, visibility: 'hidden', top: -9999, left: -9999 };
        const s = { ...boxBase, maxWidth, visibility: 'visible' };
        if (posData.left   !== undefined) s.left   = posData.left;
        if (posData.top    !== undefined) s.top    = posData.top;
        if (posData.bottom !== undefined) s.bottom = posData.bottom;
        return s;
    };

    const makeArrowDown = (arrowLeft) => ({
        position: 'absolute', bottom: '-15px', left: arrowLeft ?? '50%',
        width: 0, height: 0,
        borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
        borderTop: '15px solid white', filter: 'drop-shadow(0px 3px 0px #2D4042)',
    });

    const arrowLeftStyle = {
        position: 'absolute', top: '50%', left: '-15px',
        width: 0, height: 0,
        borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
        borderRight: '15px solid white', transform: 'translateY(-50%)',
        filter: 'drop-shadow(-3px 0px 0px #2D4042)',
    };

    const closeBtnStyle = {
        position: 'absolute', top: '6px', right: '8px',
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: '1.1rem', color: '#2D4042', fontWeight: 'bold', lineHeight: 1, padding: '2px 4px',
    };

    return (
        <div style={overlayStyle} onClick={onClose}>

            {mode === 'auto' && (
                <svg style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    <style>{`
                        @keyframes guide-pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.4; }
                        }
                        .guide-spotlight-ring { animation: guide-pulse 1.8s ease-in-out infinite; }
                    `}</style>
                    <defs>
                        <mask id="guide-spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            {spots.map((s, i) => (
                                <rect key={i}
                                    x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                                    width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                                    rx="8" fill="black"
                                />
                            ))}
                        </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(45,64,66,0.78)" mask="url(#guide-spotlight-mask)" />
                    {spots.map((s, i) => (
                        <rect key={i}
                            className="guide-spotlight-ring"
                            x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                            width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                            rx="8" fill="none" stroke="#75B9BE" strokeWidth="3"
                        />
                    ))}
                </svg>
            )}

            {/* Step 1: Upload Materials 📎 */}
            <div data-guide-popup="true" ref={box1Ref} style={getBoxStyle(positions?.box1, '280px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>📎</div>
                <p style={titleStyle}>1. Upload Materials</p>
                <p style={textStyle}>
                    Start by uploading documents for each lesson — PDFs, Word docs, or slides. The more you share, the richer the personalised tutorials ClassLabs creates!
                </p>
                <div style={makeArrowDown(positions?.box1?.arrowLeft)} />
            </div>

            {/* Step 2 (visual): Generate Lesson ✨ */}
            <div data-guide-popup="true" ref={box3Ref} style={getBoxStyle(positions?.box3, '260px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>✨</div>
                <p style={titleStyle}>2. Generate Lesson</p>
                <p style={textStyle}>
                    With your class attached, use the <strong>Generate Lesson ✨</strong> buttons to process each lesson's content and build unique tutorials tailored to every student!
                </p>
                <div style={makeArrowDown(positions?.box3?.arrowLeft)} />
            </div>

            {/* Step 3 (visual): Add Your Class 👥 */}
            <div data-guide-popup="true" ref={box4Ref} style={getBoxStyle(positions?.box4, '250px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>👥</div>
                <p style={titleStyle}>3. {anonymousProfiles ? 'Add Profiles' : 'Add Your Class'}</p>
                <p style={textStyle}>
                    {anonymousProfiles
                        ? "Click here to attach profiles to this Lesson Plan. You'll then be able to generate personalised tutorials for each lesson."
                        : "Click here to attach a class to this Lesson Plan. You'll then be able to manage students and generate personalised tutorials for each lesson."}
                </p>
                <div style={arrowLeftStyle} />
            </div>

            <p style={{ position: 'fixed', bottom: '20px', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.2rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
                Click anywhere to close
            </p>
        </div>
    );
};

export default LessonPlanGuideOverlay;
